import * as Yup from 'yup';

import { enabledMarketFields, isDestinationCountry, type OccupationIdKey } from 'utils/countries';

import { validateField, ValidationType } from './formValidation';

const OCCUPATION_ID_KEYS = ['occupation_au_id', 'occupation_ca_id', 'occupation_uk_id'] as const;

const occupationIdSchemas = (enabled: readonly string[] | null | undefined) => {
    const required = new Set<OccupationIdKey>(
        enabledMarketFields(enabled).map((market) => market.occupationIdKey)
    );
    return Object.fromEntries(
        OCCUPATION_ID_KEYS.map((key) => [
            key,
            required.has(key)
                ? Yup.number().nullable().required('Occupation / classification is required')
                : Yup.number().nullable(),
        ])
    );
};

/** BOOLEAN fields are stored as strings; Yup casts true → "true" before when(). */
const isCurrentEmployment = (current: unknown) => current === true || current === 'true';

const endDateSchema = Yup.mixed().when('current', {
    is: isCurrentEmployment,
    then: (schema) => schema.nullable().notRequired(),
    otherwise: (schema) => validateField(ValidationType.STRING),
});

/**
 * User work entry — every enabled market's occupation id is required.
 * A disabled market's id stays optional so a hidden field is not required.
 */
export const createWorkExperienceItemSchema = (enabled: readonly string[] | null | undefined) =>
    Yup.object({
        country: validateField(ValidationType.STRING),
        current: validateField(ValidationType.BOOLEAN),
        self_employed: validateField(ValidationType.BOOLEAN),
        start: validateField(ValidationType.STRING),
        percent_time: validateField(ValidationType.INTEGER_POSITIVE),
        work_permit: validateField(ValidationType.STRING, false),
        zip: validateField(ValidationType.STRING, false),
        region: Yup.string()
            .nullable()
            .when('country', {
                is: isDestinationCountry,
                then: (schema) => schema.required('Region is required'),
                otherwise: (schema) => schema.nullable(),
            }),
        end: endDateSchema,
        ...occupationIdSchemas(enabled),
        occupation_au: Yup.string().nullable(),
        occupation_ca: Yup.string().nullable(),
        occupation_uk: Yup.string().nullable(),
        occupation_au_en: Yup.string().nullable(),
        occupation_ca_en: Yup.string().nullable(),
        occupation_uk_en: Yup.string().nullable(),
    });

/** Partner work — work_permit only relevant for destination countries (PartnerWorkExperience). */
export const partnerWorkExperienceItemSchema = Yup.object({
    country: validateField(ValidationType.STRING),
    current: validateField(ValidationType.BOOLEAN),
    start: validateField(ValidationType.STRING),
    region: Yup.string().nullable(),
    end: endDateSchema,
    work_permit: Yup.string()
        .nullable()
        .when('country', {
            is: isDestinationCountry,
            then: (schema) => schema.nullable(), // optional even then in old form
            otherwise: (schema) => schema.nullable(),
        }),
});

export const createWorkExperienceValidationSchema = (
    enabled: readonly string[] | null | undefined
) =>
    Yup.object({
        has_experience: Yup.boolean().nullable(),
        user_work: Yup.array().when('has_experience', {
            is: true,
            then: (schema) => schema.of(createWorkExperienceItemSchema(enabled)).min(1),
            otherwise: () => Yup.array().notRequired(),
        }),
        spouse_work: Yup.array().of(partnerWorkExperienceItemSchema),
    });
